
import { supabase } from '@/integrations/supabase/client';
import { BrandRole } from '../types';

export const accessService = {
  async checkUserAccess(brandId: string, userId: string) {
    // First check if user has direct access record
    const { data: userAccess, error } = await supabase
      .from('user_brand_access')
      .select('role')
      .eq('brand_id', brandId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    
    // If user has access, return it
    if (userAccess) {
      return userAccess;
    }

    // If no access record, check if user owns the brand
    const { data: brand, error: brandError } = await supabase
      .from('brands')
      .select('user_id')
      .eq('id', brandId)
      .eq('user_id', userId)
      .single();

    if (brandError && brandError.code !== 'PGRST116') {
      throw brandError;
    }

    // If user owns the brand, create owner access record
    if (brand) {
      const { error: insertError } = await supabase
        .from('user_brand_access')
        .insert([{
          user_id: userId,
          brand_id: brandId,
          role: 'owner' as BrandRole,
          granted_by: userId
        }]);

      if (insertError) throw insertError;

      return { role: 'owner' as BrandRole };
    }

    return null;
  },

  async checkExistingAccess(userId: string, brandId: string) {
    console.log('=== ACCESS SERVICE: CHECKING EXISTING ACCESS ===');
    console.log('Checking existing access for:', { userId, brandId });
    
    try {
      const { data: existingAccess, error: accessError } = await supabase
        .from('user_brand_access')
        .select('id, role, user_id, brand_id')
        .eq('user_id', userId)
        .eq('brand_id', brandId)
        .maybeSingle();

      console.log('Database query result:', { existingAccess, accessError });

      if (accessError) {
        console.error('Error checking existing access:', {
          message: accessError.message,
          code: accessError.code,
          details: accessError.details,
          hint: accessError.hint
        });
        throw new Error(`Error checking existing access: ${accessError.message}`);
      }

      console.log('Existing access check result:', existingAccess);
      console.log('=== ACCESS SERVICE: EXISTING ACCESS CHECK COMPLETE ===');
      return existingAccess;

    } catch (error: any) {
      console.error('=== ACCESS SERVICE: EXISTING ACCESS CHECK ERROR ===');
      console.error('Unexpected error:', error);
      throw error;
    }
  },

  async grantAccess(userId: string, brandId: string, role: BrandRole, grantedBy: string, customRoleId?: string) {
    console.log('=== ACCESS SERVICE: GRANTING ACCESS ===');
    console.log('Granting access with details:', { userId, brandId, role, grantedBy, customRoleId });
    
    try {
      const insertData: any = {
        user_id: userId,
        brand_id: brandId,
        role: role,
        granted_by: grantedBy
      };

      if (customRoleId) {
        insertData.custom_role_id = customRoleId;
      }

      console.log('Insert data prepared:', insertData);
      console.log('Inserting into user_brand_access table...');

      const { data, error: insertError } = await supabase
        .from('user_brand_access')
        .insert([insertData])
        .select('*');

      console.log('Insert operation result:', { data, insertError });

      if (insertError) {
        console.error('=== ACCESS SERVICE: INSERT ERROR ===');
        console.error('Error inserting access record:', {
          error: insertError,
          code: insertError.code,
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint
        });
        
        // Check if it's a foreign key constraint error
        if (insertError.code === '23503') {
          console.error('Foreign key constraint violation - this usually means:');
          console.error('1. User ID does not exist in auth.users');
          console.error('2. Brand ID does not exist in brands table');
          console.error('3. Granted by user ID does not exist');
          
          throw new Error(`Database constraint error: ${insertError.message}. Please ensure the user is properly registered.`);
        }
        
        throw insertError;
      }

      console.log('Successfully granted access:', data);
      console.log('=== ACCESS SERVICE: ACCESS GRANTED SUCCESSFULLY ===');
      return true;

    } catch (error: any) {
      console.error('=== ACCESS SERVICE: GRANT ACCESS ERROR ===');
      console.error('Unexpected error in grantAccess:', error);
      throw error;
    }
  }
};
